import React, { useEffect, useState } from 'react';
import {
  Card,
  Steps,
  Upload,
  Form,
  Input,
  Button,
  Table,
  Alert,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Statistic,
  Select,
  App
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useImportBOQ } from '../../hooks/useBOQ';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { useOpportunities } from '../../hooks/useOpportunities';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import { ProjectSelector } from '../../components/project';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import {
  parseBoqImportFile,
  downloadBoqLineImportTemplate,
  type BoqLineImportPreviewRow,
} from '@/utils/boqLineImportParser';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

type PreviewRow = BoqLineImportPreviewRow;

const BOQImportWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { allCurrencies, baseCurrency, formatCurrency } = useCurrency();
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<{ id: string; project_name: string } | null>(null);

  const importMutation = useImportBOQ();
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();
  const { data: opportunities = [], isLoading: isLoadingOpportunities } = useOpportunities();

  const opportunityIdFromUrl = searchParams.get('opportunity_id')?.trim() || undefined;

  useEffect(() => {
    if (opportunityIdFromUrl) {
      form.setFieldsValue({ opportunityId: opportunityIdFromUrl });
    }
  }, [opportunityIdFromUrl, form]);

  useEffect(() => {
    if (!opportunityIdFromUrl) return;
    const linkedOpp = opportunities.find((o) => o.id === opportunityIdFromUrl);
    if (!linkedOpp?.customer_id) return;
    form.setFieldsValue({ customerId: linkedOpp.customer_id });
  }, [opportunityIdFromUrl, opportunities, form]);

  // Step 1: File Upload
  const uploadProps = {
    accept: '.xlsx,.xls,.csv',
    maxCount: 1,
    beforeUpload: (file: File) => {
      // Validate file
      const isExcelOrCSV =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv';

      if (!isExcelOrCSV) {
        message.error('You can only upload Excel or CSV files!');
        return Upload.LIST_IGNORE;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!');
        return Upload.LIST_IGNORE;
      }

      setFile(file);
      setUploadedFile({
        uid: '-1',
        name: file.name,
        status: 'done',
        size: file.size,
        type: file.type
      } as UploadFile);

      // Parse and preview (simplified - in real app would use xlsx library)
      parseFile(file);

      return false; // Prevent auto upload
    },
    onRemove: () => {
      setFile(null);
      setUploadedFile(null);
      setPreviewData([]);
    },
    fileList: uploadedFile ? [uploadedFile] : []
  };

  const parseFile = async (file: File) => {
    try {
      const parsedData = await parseBoqImportFile(file);
      if (parsedData.length === 0) {
        message.warning('No valid data found in the file');
      } else {
        const validCount = parsedData.filter((row) => row.status === 'valid').length;
        const errorCount = parsedData.filter((row) => row.status === 'error').length;
        message.success(`Parsed ${validCount} valid rows${errorCount > 0 ? ` (${errorCount} errors)` : ''}`);
      }
      setPreviewData(parsedData);
    } catch (error) {
      console.error('Error parsing file:', error);
      message.error('Failed to parse file. Please check the format.');
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (!file) {
        message.error('Please upload a file');
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      if (previewData.length === 0) {
        message.error('No data to import');
        return;
      }
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleImport = async () => {
    try {
      const values = await form.validateFields();
      console.log('[BOQImportWizard] Form values:', values);

      if (!file) {
        message.error('No file selected');
        return;
      }

      // Get customer name from customerId
      const customerName = values.customerId
        ? customers?.find(c => c.id === values.customerId)?.customer_name
        : undefined;

      // Get project name from selected project or form value
      const projectName = selectedProject?.project_name || values.projectName;

      const linkedOppId =
          typeof values.opportunityId === 'string' && values.opportunityId.trim()
            ? values.opportunityId.trim()
            : undefined;
      const linkedOpp = linkedOppId
        ? opportunities.find((o) => o.id === linkedOppId)
        : undefined;

      const importData = {
        file,
        boqName: values.boqName,
        customerId: values.customerId,
        customerName: customerName,
        projectId: values.projectId,
        projectName: projectName,
        currency: values.currency || baseCurrency?.currency_code || 'USD',
        opportunityId: linkedOppId,
        salesTeamId: linkedOpp?.sales_team_id || undefined,
      };

      console.log('[BOQImportWizard] Sending import data:', {
        ...importData,
        file: { name: file.name, size: file.size, type: file.type }
      });

      const response = await importMutation.mutateAsync(importData);

      // The mutation returns the ImportResult directly (not nested under .data)
      const result = response;
      setImportResult(result);
      setCurrentStep(3);

      // Navigate to the new BOQ after a delay
      if (result?.boqId) {
        setTimeout(() => {
          navigate('/growth/estimations/boq');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      const data = error?.response?.data;
      const code = error?.code as string | undefined;
      const noResponseHint =
        !error?.response &&
        (code === 'ERR_NETWORK' ||
          code === 'ECONNABORTED' ||
          /network/i.test(String(error?.message || '')))
          ? ` Cannot reach the API (${code || error?.message || 'no response'}) — check that the backend is running. Request base URL: ${API_BASE_URL}. Override with VITE_API_URL if needed.`
          : '';
      const errorMessage =
        (typeof data?.message === 'string' ? data.message : null) ||
        data?.error ||
        error?.message ||
        'Failed to import BOQ';
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      const suffix = noResponseHint;
      message.error(
        errors.length > 0
          ? `${errorMessage}: ${errors.slice(0, 3).join('; ')}${suffix}`
          : `${errorMessage}${suffix}`
      );
    }
  };

  const downloadTemplate = () => {
    downloadBoqLineImportTemplate('BOQ_Template.csv');
  };

  const previewColumns: ColumnsType<PreviewRow> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150
    },
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 120
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 160,
      ellipsis: true,
      render: (v: string) => v || '—'
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 80,
      render: (v: string) => v || '—'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right'
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      key: 'unitCost',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(value)
    },
    {
      title: 'Line Total',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(value)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record: PreviewRow) => (
        <>
          {status === 'valid' ? (
            <Tag color="success">Valid</Tag>
          ) : (
            <Tag color="error" title={record.errorMessage}>
              Error
            </Tag>
          )}
        </>
      )
    }
  ];

  const steps = [
    {
      title: 'Upload File',
      icon: <UploadOutlined />
    },
    {
      title: 'Preview Data',
      icon: <CheckCircleOutlined />
    },
    {
      title: 'BOQ Details',
      icon: <CheckCircleOutlined />
    },
    {
      title: 'Complete',
      icon: <CheckCircleOutlined />
    }
  ];

  const validRows = previewData.filter(row => row.status === 'valid').length;
  const errorRows = previewData.filter(row => row.status === 'error').length;
  const totalAmount = previewData.reduce((sum, row) => sum + row.lineTotal, 0);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/growth/estimations/boq')}
          style={{ marginBottom: 16 }}
        >
          Back to List
        </Button>

        <Title level={2}>Import BOQ from Excel</Title>
        <Paragraph type="secondary">
          Upload an Excel or CSV file with your Bill of Quantities data
        </Paragraph>

        <Divider />

        <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

        {/* Step 0: Upload File */}
        {currentStep === 0 && (
          <div>
            <Row gutter={24}>
              <Col span={16}>
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined style={{ fontSize: 48, color: '#145A54' }} />
                  </p>
                  <p className="ant-upload-text">Click or drag file to upload</p>
                  <p className="ant-upload-hint">
                    Support for Excel (.xlsx, .xls) and CSV files. Maximum file size: 10MB
                  </p>
                </Dragger>
              </Col>
              <Col span={8}>
                <Card title="Excel Template Format" size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Required Columns:</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Category</li>
                      <li>Item Name</li>
                      <li>Quantity</li>
                      <li>UOM</li>
                      <li>Unit Cost</li>
                    </ul>
                    <Text strong>Optional Columns:</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Item Code</li>
                      <li>Description</li>
                      <li>Supplier</li>
                      <li>Lead Time</li>
                    </ul>
                    <Button
                      block
                      icon={<DownloadOutlined />}
                      onClick={downloadTemplate}
                      style={{ marginTop: 16 }}
                    >
                      Download Template
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Step 1: Preview Data */}
        {currentStep === 1 && (
          <div>
            {errorRows > 0 && (
              <Alert
                message="Validation Errors Found"
                description={`${errorRows} row(s) have errors and will be skipped. Please review and fix the errors in your file.`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic title="Total Rows" value={previewData.length} />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic title="Valid Rows" value={validRows} valueStyle={{ color: '#52c41a' }} />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Total Amount"
                    value={totalAmount}
                    prefix={
                      String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                        <AedDirhamSign />
                      ) : (
                        (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                      )
                    }
                    precision={2}
                  />
                </Card>
              </Col>
            </Row>

            <Table
              columns={previewColumns}
              dataSource={previewData}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}

        {/* Step 2: BOQ Details */}
        {currentStep === 2 && (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (!('opportunityId' in changed)) return;
              const nextOppId = String(changed.opportunityId || '').trim();
              if (!nextOppId) return;
              const linkedOpp = opportunities.find((o) => o.id === nextOppId);
              if (linkedOpp?.customer_id) {
                form.setFieldValue('customerId', linkedOpp.customer_id);
              }
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="boqName"
                  label="BOQ Name"
                  rules={[{ required: true, message: 'Please enter BOQ name' }]}
                >
                  <Input placeholder="e.g., Office Furniture Supply 2025" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customerId" label="Customer Name">
                  <Select
                    showSearch
                    placeholder="Select customer"
                    loading={isLoadingCustomers}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? option?.children ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                    }
                  >
                    {customers?.map((customer) => (
                      <Option key={customer.id} value={customer.id}>
                        {customer.customer_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="opportunityId" label="Opportunity (optional)">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Link to CRM opportunity"
                    loading={isLoadingOpportunities}
                    optionFilterProp="label"
                    options={opportunities.map((o) => ({
                      value: o.id,
                      label: `${o.opportunity_name} (${o.opportunity_number}) — ${o.stage}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="projectId" label="Project">
                  <ProjectSelector
                    value={form.getFieldValue('projectId')}
                    onChange={(projectId, projectData) => {
                      if (projectId && projectData) {
                        form.setFieldsValue({ 
                          projectId, 
                          projectName: projectData.project_name 
                        });
                        setSelectedProject({ id: projectId, project_name: projectData.project_name });
                      } else {
                        form.setFieldsValue({ 
                          projectId: undefined,
                          projectName: undefined 
                        });
                        setSelectedProject(null);
                      }
                    }}
                    customerId={form.getFieldValue('customerId')}
                    customerName={customers?.find(c => c.id === form.getFieldValue('customerId'))?.customer_name}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  rules={[{ required: true, message: 'Please select currency' }]}
                  initialValue={baseCurrency?.currency_code || 'USD'}
                >
                  <Select placeholder="Select currency" showSearch>
                    {allCurrencies.map((currency) => (
                      <Option key={currency.id} value={currency.currency_code}>
                        {currency.currency_code} - {currency.currency_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Alert
              message="Import Summary"
              description={
                <div>
                  <p>
                    <strong>File:</strong> {file?.name}
                  </p>
                  <p>
                    <strong>Valid Items:</strong> {validRows} items
                  </p>
                  <p>
                    <strong>Total Amount:</strong> {formatCurrency(totalAmount)}
                  </p>
                </div>
              }
              type="info"
              showIcon
            />
          </Form>
        )}

        {/* Step 3: Complete */}
        {currentStep === 3 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 72, color: '#52c41a', marginBottom: 24 }} />
            <Title level={3}>Import Successful!</Title>
            {importResult && (
              <div>
                <Paragraph>
                  {importResult.message || 'BOQ has been created successfully.'}
                </Paragraph>
                {importResult.statistics && (
                  <Space direction="vertical" style={{ marginTop: 24 }}>
                    <Text>
                      <strong>Categories:</strong> {importResult.statistics.categories}
                    </Text>
                    <Text>
                      <strong>Items:</strong> {importResult.statistics.items}
                    </Text>
                    <Text>
                      <strong>Valid Rows:</strong> {importResult.statistics.validRows} of{' '}
                      {importResult.statistics.totalRows}
                    </Text>
                  </Space>
                )}
                <Divider />
                <Space>
                  <Button onClick={() => navigate('/growth/estimations/boq')}>Back to List</Button>
                  {importResult.boqId && (
                    <Button
                      type="primary"
                      onClick={() => navigate(`/growth/estimations/boq/edit/${importResult.boqId}`)}
                    >
                      View BOQ
                    </Button>
                  )}
                </Space>
              </div>
            )}
          </div>
        )}

        <Divider />

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => navigate('/growth/estimations/boq')}>Cancel</Button>
            <Space>
              {currentStep > 0 && currentStep < 3 && (
                <Button onClick={handlePrevious}>Previous</Button>
              )}
              {currentStep < 2 && (
                <Button type="primary" onClick={handleNext} disabled={currentStep === 0 && !file}>
                  Next
                </Button>
              )}
              {currentStep === 2 && (
                <Button
                  type="primary"
                  onClick={handleImport}
                  loading={importMutation.isPending}
                >
                  Import BOQ
                </Button>
              )}
            </Space>
          </Space>
        )}
      </Card>
    </div>
  );
};

export default BOQImportWizard;

